import { useEffect, useState } from "react";
import { AutomationStatusType } from "@/types/graphql/partner.generated";
import { AUTOMATION_POLL_INTERVAL_MS } from "./automationStats";

/** То, что от документа нужно опросу: видимость и её смена. */
export type VisibilitySource = Pick<
  Document,
  "visibilityState" | "addEventListener" | "removeEventListener"
>;

const browserDocument = (): VisibilitySource | null =>
  typeof document === "undefined" ? null : document;

/** Страница на экране: скрытая вкладка браузера не опрашивает сервер. */
export const pageVisible = (source: VisibilitySource | null): boolean =>
  source === null || source.visibilityState !== "hidden";

/**
 * Подписка на смену видимости (visibilitychange): колбэк получает новое
 * значение, возврат снимает подписку.
 */
export function subscribeVisibility(
  source: VisibilitySource,
  onChange: (visible: boolean) => void
): () => void {
  const listener = () => onChange(pageVisible(source));
  source.addEventListener("visibilitychange", listener);
  return () => source.removeEventListener("visibilitychange", listener);
}

/** Видна ли вкладка браузера сейчас, с обновлением по visibilitychange. */
export function usePageVisible(
  source: VisibilitySource | null = browserDocument()
): boolean {
  const [visible, setVisible] = useState(() => pageVisible(source));
  useEffect(
    () => (source ? subscribeVisibility(source, setVisible) : undefined),
    [source]
  );
  return visible;
}

interface LivePollState {
  status: AutomationStatusType | null | undefined;
  /** Диалог открыт (закрытый диалог размонтирован, но правило явное). */
  open: boolean;
  visible: boolean;
}

/**
 * Интервал опроса открытой автоматизации (супер-ревью 4): только у
 * запущенной, только пока диалог открыт и вкладка браузера видна; иначе
 * опроса нет (0).
 */
export const livePollInterval = ({
  status,
  open,
  visible,
}: LivePollState): number =>
  open && visible && status === AutomationStatusType.ActiveType
    ? AUTOMATION_POLL_INTERVAL_MS
    : 0;
